/**
 * Wire shape for every add-in telemetry event, plus the closed catalogues of
 * event names and property keys that gate what may be logged.
 *
 * ─── How safety is enforced ───────────────────────────────────────────────
 * 1. `EventName` is `typeof EVENT_NAMES[number]`. Off-list strings are a
 *    TypeScript compile error — there is no escape hatch short of casting to
 *    `any`, which is forbidden by the project ESLint rules.
 * 2. `AllowedPropertyKey` is `typeof ALLOWED_PROPERTY_KEYS[number]`. The
 *    `properties` bag accepts only these keys, so a property like
 *    `customerName` or `fileTitle` breaks the build at the call site rather
 *    than leaking PII at runtime.
 * 3. The server-side scrubber (`Api/Telemetry.Endpoints/Services/PiiScrubber`)
 *    is defence in depth — but the client-side type system is the first and
 *    most important gate.
 *
 * Any change to the closed lists below requires code review.
 */

/** Wire-format version. Bump when the envelope shape changes. */
export const TELEMETRY_SCHEMA_VERSION = 1 as const;

/** Closed enum of event kinds. Maps to Serilog log levels server-side. */
export const EVENT_KINDS = ["usage", "error", "performance", "warn", "info"] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

/** Office host the add-in is running inside. */
export const ADDIN_HOSTS = ["outlook", "word", "powerpoint"] as const;
export type AddInHost = (typeof ADDIN_HOSTS)[number];

/**
 * Which backend an event relates to. Tagged on api-call and api-error events.
 *   - `addin`       — our .NET Addin.Endpoints surface (settings, bridge calls).
 *   - `ai`          — the Python AI API (chat, skill execution).
 *   - `telemetry`   — this very pipeline (used by the transport for self-logging).
 *   - `integration` — third-party services reached through `serviceFetch`
 *                     (engage, myqueue, logocloud, docuparser, transcribe,
 *                     deckorate). They're not our infra, so we tag them
 *                     distinctly for KQL slicing.
 */
export const BACKENDS = ["addin", "ai", "telemetry", "integration"] as const;
export type Backend = (typeof BACKENDS)[number];

/**
 * Closed catalogue of property keys that may appear in
 * {@link TelemetryEvent.properties}. Anything off this list is a TypeScript
 * compile error at the call site.
 *
 * Add a key only after confirming it cannot leak customer content:
 *   - Counts (bucketed where unbounded).
 *   - Identifiers from our own taxonomy (skillId, moduleId, ...).
 *   - Templated paths (`/api/skills/{id}/run` — never the actual id).
 *   - Closed-enum tokens (errorCode, reason).
 */
export const ALLOWED_PROPERTY_KEYS = [
  // Domain identifiers (from our taxonomy, never user-provided)
  "skillId",
  "skillGroupId",
  "moduleId",
  "workflowId",
  "connectorSystemName",
  "panelName",
  // Counts (bucketed for unbounded sources)
  "characterCount",
  "tokenCount",
  "paramCount",
  "fileSizeBucket",
  "attachmentCount",
  "hasAttachment",
  "fileType",
  // Timing
  "durationMs",
  "clientDurationMs",
  "elapsedMs",
  // API call metadata (templated path only — never the real id in the URL)
  "path",
  "method",
  "statusCode",
  "backend",
  // Closed-enum codes
  "errorCode",
  "reason",
  // Pipeline metadata
  "samplingRate",
  "capabilityId",
  "outcome",
  "surface",
  "trigger",
  "writePath",
  "committed",
  "wroteToDocument",
  "aiUsed",
  "graphUsed",
  "barVariant",
  "apiSetRequired",
  "confirmShown",
  "confirmAccepted",
  "recipientCount",
  "externalCount",
  "hitCount",
  "hitTotal",
  "threadMessages",
  "threadMessagesExact",
  "truncated",
  "matchCount",
  "appliedCount",
  "sectionCount",
  "termCount",
  "shapeCount",
  "hostVersion",
] as const;
export type AllowedPropertyKey = (typeof ALLOWED_PROPERTY_KEYS)[number];

/** Permissible values for a property bag entry. */
export type AllowedPropertyValue = string | number | boolean | null;

/** The full closed catalogue of event names. */
export const EVENT_NAMES = [
  // lifecycle
  "app.boot",
  "app.shutdown",
  "app.auth.success",
  "app.auth.failure",
  "app.settings.loaded",
  "app.settings.load-failed",
  // user interaction
  "nav.panel.open",
  "nav.module.select",
  "skill.invoke",
  "chat.message.sent",
  "chat.response.received",
  "workflow.submit",
  "capability.invoke",
  "capability.outcome",
  "connector.upload",
  "connector.download",
  "connector.action.invoke",
  // performance
  "perf.api.call",
  "perf.api.cancelled",
  "perf.skill.execute",
  "perf.render.first-paint",
  // error
  "error.unhandled",
  "error.promise-rejection",
  "error.react.boundary",
  "error.office-js",
  "error.api.4xx",
  "error.api.5xx",
  "error.network",
  "error.connector.failure",
  "error.sso.discovery",
] as const;
export type EventName = (typeof EVENT_NAMES)[number];

/**
 * Error block attached when {@link TelemetryEvent.kind} is `"error"`.
 *
 * `scrubbedMessage` must already be PII-stripped by the client (see
 * `scrub.ts`) before being placed here.
 */
export interface TelemetryError {
  type: string;
  scrubbedMessage: string;
}

/**
 * Identity bundle resolved once at boot from Office.context.diagnostics plus
 * the signed-in session. Carries surface / runtime identifiers and — for the
 * Studio Logs page's User column — the user's email.
 *
 * <b>Why the email is sourced client-side (AIVA-7822).</b> The platform's Entra
 * Claims Mapping Policy strips `email` and `preferred_username` from the API
 * <em>access</em> token, and the Telemetry ingest service holds no user database,
 * so the server cannot resolve the email for an add-in row — it would render
 * "system". The signed-in session's <em>id</em> token still carries the email
 * (across every IdP: `email` / `preferred_username` / `upn`), so the add-in reads
 * it from there and binds it here. This is display attribution for an
 * observability surface, NOT the security audit-of-record (that stays the
 * server-resolved `AuditLog.UserEmail`). The APIM-validated bearer's `oid` — which
 * is NOT stripped — remains the server-trusted anchor for the same row.
 *
 * Tenant fields are absent because AiVA Studio deploys single-tenant per customer.
 */
export interface TelemetryIdentity {
  addInHost: AddInHost;
  addInVersion: string;
  officeJsVersion: string;
  platform: string;
  /** Signed-in user's email/UPN, read from the id-token claims (any IdP). */
  userEmail?: string;
}

/** Typed property bag — keys constrained by {@link ALLOWED_PROPERTY_KEYS}. */
export type TelemetryProperties = Partial<Record<AllowedPropertyKey, AllowedPropertyValue>>;

/**
 * The wire envelope. Carries surface + session context and the signed-in user's
 * email for the Studio Logs page's User column — see {@link TelemetryIdentity}
 * for why the email is client-sourced (the access token is stripped of it and
 * the ingest service has no user database). The server sanitises the value and
 * keeps the bearer's `oid` as the trusted anchor, so a tampered client can at
 * worst mislabel its own observability rows — it cannot forge audit-of-record
 * identity, which stays server-resolved.
 */
export interface TelemetryEvent {
  schemaVersion: typeof TELEMETRY_SCHEMA_VERSION;
  eventId: string;
  occurredAtUtc: string;
  kind: EventKind;
  name: EventName;
  // Surface
  addInHost: AddInHost;
  addInVersion: string;
  officeJsVersion: string;
  platform: string;
  // Identity (display attribution; oid on the bearer is the server-trusted anchor)
  userEmail?: string;
  // Correlation
  sessionId: string;
  correlationId?: string;
  // Error block — populated only when kind === "error"
  error?: TelemetryError;
  // Allow-listed property bag
  properties?: TelemetryProperties;
}

/** Runtime check that a property key is on the allow-list. */
export function isAllowedPropertyKey(key: string): key is AllowedPropertyKey {
  return (ALLOWED_PROPERTY_KEYS as readonly string[]).includes(key);
}

/** Runtime check that an event name is on the catalogue. */
export function isEventName(name: string): name is EventName {
  return (EVENT_NAMES as readonly string[]).includes(name);
}
